import { useCallback, useEffect, useState } from "react";
import Cropper from "react-easy-crop";
import { motion } from "framer-motion";
import { X, RotateCw, Check, Sun, Contrast, Droplet, Crop as CropIcon } from "lucide-react";

// Uploads straight off a phone are routinely 3-5 MB. Re-encoding to WebP at a
// sane size typically cuts that by an order of magnitude with no visible loss.
const MAX_DIMENSION = 1600;
const QUALITY = 0.9;

const ASPECTS = [
  { label: "4:3", value: 4 / 3, hint: "cartão de produto" },
  { label: "1:1", value: 1, hint: "galeria" },
  { label: "16:9", value: 16 / 9, hint: "banner" },
  { label: "Livre", value: null, hint: "sem recorte fixo" },
];

const DEFAULT_ADJUSTMENTS = { brightness: 100, contrast: 100, saturate: 100 };

const filterString = (a) =>
  `brightness(${a.brightness}%) contrast(${a.contrast}%) saturate(${a.saturate}%)`;

const loadImage = (src) =>
  new Promise((resolve, reject) => {
    const image = new Image();
    image.addEventListener("load", () => resolve(image));
    image.addEventListener("error", () => reject(new Error("imagem inválida")));
    image.src = src;
  });

/**
 * Crop, rotate and adjust, then re-encode.
 *
 * The rotation is baked on an intermediate canvas first, so the crop rectangle
 * that react-easy-crop reports lines up with what the user actually sees.
 */
async function render(src, area, rotation, adjustments) {
  const image = await loadImage(src);
  const radians = (rotation * Math.PI) / 180;
  const sin = Math.abs(Math.sin(radians));
  const cos = Math.abs(Math.cos(radians));

  const rotated = document.createElement("canvas");
  rotated.width = image.width * cos + image.height * sin;
  rotated.height = image.width * sin + image.height * cos;
  const rctx = rotated.getContext("2d");
  rctx.translate(rotated.width / 2, rotated.height / 2);
  rctx.rotate(radians);
  rctx.drawImage(image, -image.width / 2, -image.height / 2);

  let { width, height } = area;
  const scale = Math.min(1, MAX_DIMENSION / Math.max(width, height));
  const out = document.createElement("canvas");
  out.width = Math.round(width * scale);
  out.height = Math.round(height * scale);

  const octx = out.getContext("2d");
  octx.imageSmoothingQuality = "high";
  // Not supported by very old Safari; the image is simply left unadjusted there.
  octx.filter = filterString(adjustments);
  octx.drawImage(rotated, area.x, area.y, width, height, 0, 0, out.width, out.height);

  return new Promise((resolve, reject) => {
    out.toBlob(
      (blob) => (blob ? resolve(blob) : reject(new Error("falha a processar a imagem"))),
      "image/webp",
      QUALITY,
    );
  });
}

const Slider = ({ icon: Icon, label, value, min, max, onChange, suffix = "%" }) => (
  <label className="flex items-center gap-3 text-sm">
    <Icon size={16} className="text-muted-foreground shrink-0" />
    <span className="w-20 shrink-0">{label}</span>
    <input type="range" min={min} max={max} value={value}
      onChange={(e) => onChange(Number(e.target.value))}
      className="flex-1 accent-[#7000FF]" />
    <span className="w-12 text-right tabular-nums text-muted-foreground">{value}{suffix}</span>
  </label>
);

export const ImageEditor = ({ file, index, total, onCancel, onDone }) => {
  const [src, setSrc] = useState(null);
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [rotation, setRotation] = useState(0);
  const [aspect, setAspect] = useState(4 / 3);
  const [adjustments, setAdjustments] = useState(DEFAULT_ADJUSTMENTS);
  const [area, setArea] = useState(null);
  const [working, setWorking] = useState(false);

  useEffect(() => {
    if (!file) return undefined;
    const url = URL.createObjectURL(file);
    setSrc(url);
    setCrop({ x: 0, y: 0 });
    setZoom(1);
    setRotation(0);
    setAdjustments(DEFAULT_ADJUSTMENTS);
    return () => URL.revokeObjectURL(url);
  }, [file]);

  const onCropComplete = useCallback((_, pixels) => setArea(pixels), []);

  const apply = async () => {
    if (!area) return;
    setWorking(true);
    try {
      const blob = await render(src, area, rotation, adjustments);
      const name = file.name.replace(/\.[^.]+$/, "") + ".webp";
      onDone(new File([blob], name, { type: "image/webp" }));
    } catch (err) {
      onDone(null, err);
    } finally {
      setWorking(false);
    }
  };

  const set = (k, v) => setAdjustments((a) => ({ ...a, [k]: v }));

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      data-testid="image-editor"
      className="fixed inset-0 z-[70] bg-black/70 backdrop-blur-sm grid place-items-center p-4">
      <motion.div initial={{ scale: 0.96, y: 16 }} animate={{ scale: 1, y: 0 }}
        onClick={(e) => e.stopPropagation()}
        className="bg-card rounded-[2rem] border border-border w-full max-w-3xl max-h-[92vh] overflow-y-auto">

        <div className="flex items-center justify-between p-5 border-b border-border">
          <h2 className="font-display font-bold text-lg flex items-center gap-2">
            <CropIcon size={18} /> Ajustar imagem
            {total > 1 && <span className="text-sm font-normal text-muted-foreground">({index + 1} de {total})</span>}
          </h2>
          <button onClick={onCancel} data-testid="editor-cancel" className="p-2 rounded-full hover:bg-muted"><X size={18} /></button>
        </div>

        <div className="relative h-[46vh] min-h-[260px] bg-black">
          {src && (
            <Cropper
              image={src}
              crop={crop}
              zoom={zoom}
              rotation={rotation}
              aspect={aspect ?? undefined}
              onCropChange={setCrop}
              onZoomChange={setZoom}
              onRotationChange={setRotation}
              onCropComplete={onCropComplete}
              restrictPosition={!!aspect}
              style={{ mediaStyle: { filter: filterString(adjustments) } }}
            />
          )}
        </div>

        <div className="p-5 space-y-4">
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-sm font-semibold mr-1">Formato</span>
            {ASPECTS.map((a) => (
              <button key={a.label} onClick={() => setAspect(a.value)} title={a.hint}
                data-testid={`aspect-${a.label}`}
                className={`rounded-full px-4 py-1.5 text-sm font-semibold border transition-colors ${
                  aspect === a.value ? "brand-gradient text-white border-transparent" : "border-border text-muted-foreground hover:bg-muted"
                }`}>
                {a.label}
              </button>
            ))}
            <button onClick={() => setRotation((r) => (r + 90) % 360)}
              data-testid="editor-rotate"
              className="ml-auto inline-flex items-center gap-2 rounded-full border border-border px-4 py-1.5 text-sm font-semibold hover:bg-muted transition-colors">
              <RotateCw size={15} /> Rodar
            </button>
          </div>

          <div className="space-y-2.5">
            <Slider icon={CropIcon} label="Zoom" value={Math.round(zoom * 100)} min={100} max={300}
              onChange={(v) => setZoom(v / 100)} />
            <Slider icon={Sun} label="Brilho" value={adjustments.brightness} min={50} max={150}
              onChange={(v) => set("brightness", v)} />
            <Slider icon={Contrast} label="Contraste" value={adjustments.contrast} min={50} max={150}
              onChange={(v) => set("contrast", v)} />
            <Slider icon={Droplet} label="Saturação" value={adjustments.saturate} min={0} max={200}
              onChange={(v) => set("saturate", v)} />
          </div>

          <button onClick={() => { setAdjustments(DEFAULT_ADJUSTMENTS); setRotation(0); setZoom(1); }}
            className="text-xs text-muted-foreground underline hover:text-foreground">
            repor ajustes
          </button>
        </div>

        <div className="p-5 border-t border-border flex justify-end gap-3">
          <button onClick={onCancel} className="rounded-full border border-border px-5 py-3 font-semibold hover:bg-muted transition-colors">
            {total > 1 ? "Ignorar esta" : "Cancelar"}
          </button>
          <button onClick={apply} disabled={working || !area} data-testid="editor-apply"
            className="inline-flex items-center gap-2 rounded-full brand-gradient text-white font-bold px-6 py-3 transition-transform hover:scale-105 active:scale-95 disabled:opacity-60">
            <Check size={18} /> {working ? "A processar..." : "Aplicar e carregar"}
          </button>
        </div>
      </motion.div>
    </motion.div>
  );
};
